import { useState, useEffect, useCallback, useRef } from 'react';
import { apiDelete, apiGet, apiPost, apiPut, getToken } from '../api';

function localNow() {
  var d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + 'T' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0');
}

export function useCollectionApi(collectionName) {
  const opts = arguments.length > 1 && arguments[1] ? arguments[1] : {};
  const enabled = opts.enabled !== false;
  const pollMs = typeof opts.pollMs === 'number' ? opts.pollMs : 30000;
  const initialDelayMs = typeof opts.initialDelayMs === 'number' ? opts.initialDelayMs : 0;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const startupTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    if (!getToken()) return;
    try {
      const res = await apiGet('/api/collections/' + collectionName);
      setData(res.items || []);
    } catch (err) {
      console.error('Load error:', err);
    }
  }, [collectionName, enabled]);

  useEffect(() => {
    var mounted = true;

    if (!enabled) {
      setLoading(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (startupTimerRef.current) {
        clearTimeout(startupTimerRef.current);
        startupTimerRef.current = null;
      }
      return function() {
        mounted = false;
      };
    }

    if (!getToken()) {
      setLoading(false);
      return function() {
        mounted = false;
      };
    }

    setLoading(true);
    var startLoad = function() {
      load()
        .catch(function(err) {
          console.error('Initial load error:', err);
        })
        .finally(function() {
          if (mounted) setLoading(false);
        });
    };

    if (initialDelayMs > 0) {
      startupTimerRef.current = setTimeout(startLoad, initialDelayMs);
    } else {
      startLoad();
    }

    if (pollMs > 0) {
      timerRef.current = setInterval(function() {
        load().catch(function(err) {
          console.error('Polling load error:', err);
        });
      }, pollMs);
    }

    return function() {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (startupTimerRef.current) clearTimeout(startupTimerRef.current);
    };
  }, [collectionName, load, enabled, pollMs, initialDelayMs]);

  const addItem = useCallback(async (item) => {
    const payload = {
      ...item,
      createdAt: localNow(),
    };
    const res = await apiPost('/api/collections/' + collectionName, payload);
    const created = res.item || payload;
    setData(function(prev) { return [created].concat(prev); });
    return created;
  }, [collectionName]);

  const updateItem = useCallback(async (id, updates) => {
    await apiPut('/api/collections/' + collectionName + '/' + id, {
      ...updates,
      updatedAt: localNow(),
    });
    setData(prev => prev.map(d => d._id === id ? { ...d, ...updates } : d));
  }, [collectionName]);

  const deleteItem = useCallback(async (id) => {
    await apiDelete('/api/collections/' + collectionName + '/' + id);
    setData(prev => prev.filter(d => d._id !== id));
  }, [collectionName]);

  const bulkUpload = useCallback(async (items) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += 300) {
      chunks.push(items.slice(i, i + 300));
    }
    for (const chunk of chunks) {
      try {
        await apiPost('/api/collections/' + collectionName + '/bulk', { items: chunk });
      } catch (err) {
        console.error('Bulk upload error:', err);
        throw err;
      }
    }
    await load();
  }, [collectionName]);

  const replaceAll = useCallback(async (items) => {
    await apiPost('/api/collections/' + collectionName + '/replace', { items: items || [] });
    await load();
  }, [collectionName, load]);

  return {
    data,
    loading,
    addItem,
    updateItem,
    deleteItem,
    bulkUpload,
    replaceAll,
    reload: load,
  };
}